import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';

// API base URL determination
const getApiUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8010';
  }
  return window.location.origin.replace(':8090', ':8000');
};

const API_URL = getApiUrl();

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if user is already authenticated on component mount
  useEffect(() => {
    checkAuthentication();
  }, []);
  
  // Save original URL with query parameters
  useEffect(() => {
    if (!isAuthenticated && location.search) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
  }, [location, isAuthenticated]);
  
  const checkAuthentication = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      localStorage.removeItem('admin_token');
    }
    
    setIsLoading(false);
  };
  
  const handleLogin = async () => {
    if (!password.trim()) {
      setError('Bitte geben Sie ein Passwort ein.');
      return;
    }
    
    setIsLoggingIn(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: password }),
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('admin_token', data.access_token);
        setIsAuthenticated(true);
        setError('');
        
        // Check if there's a redirect URL saved
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Falsches Passwort. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError('Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    }
    
    setIsLoggingIn(false);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoggingIn) {
      handleLogin();
    }
  };
  
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>
            Überprüfung der Authentifizierung...
          </Typography>
        </Box>
      </Container>
    );
  }
  
  if (isAuthenticated) {
    return children;
  }
  
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LockIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
            <Typography component="h1" variant="h5" gutterBottom>
              Admin-Bereich
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Bitte geben Sie das Passwort ein, um auf den Admin-Bereich zuzugreifen.
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Passwort"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoggingIn}
              autoFocus
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleLogin}
              disabled={isLoggingIn}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoggingIn ? <CircularProgress size={24} /> : 'Anmelden'}
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              component={Link}
              to="/"
              disabled={isLoggingIn}
              sx={{ mt: 1 }}
            >
              Zurück zur Startseite
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProtectedRoute;